import { Component, OnDestroy, OnInit } from '@angular/core';

import {
  BehaviorSubject,
  catchError,
  combineLatest,
  filter,
  of,
  map,
  Subscription,
  switchMap,
  tap,
  startWith,
  pairwise,
  finalize,
  combineLatestWith, take,
} from 'rxjs';

import { UserAccountRole } from '@shared/user/enums';
import { User } from '@shared/user/models';
import { Location } from '@shared/location/models';
import {
  ConfirmationModalComponent,
  ConfirmationModalResult,
  OrderByValues,
  UserWithAccount,
} from '@tablesready/common-ui';
import { UserFilters } from '../../models';
import { InviteUserComponent } from '../../modals';
import { UsersFilterService } from './users.filter.service';
import { UsersListService } from './users-list.service';
import { EditUserAccessService, InvitesService } from '../../services';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor } from '@tablesready/frontend-icons';
import {
  AccountApiService,
  AuthRestaurantService,
  AuthUserService,
} from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';

@Component({
  selector: 'tablesready-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
})
export class UsersListComponent implements OnInit, OnDestroy {
  page = 1;
  pageSize = 10;
  maxSize = 10;
  icon = AppIcon;
  color = AppIconColor;
  users: UserWithAccount[] = [];
  locations: Location[];
  locationsMap: Record<string, Location>;
  userLocationsTooltips: Record<string, string> = {};
  currentUser: User;
  selectedLocationId?: string;
  fullUsersList: UserWithAccount[] = [];

  filters$ = new BehaviorSubject<UserFilters>({
    search: '',
    locationId: null,
    sortBy: null,
    sortReverse: false,
  });
  isEditingUser = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly modalController: FrontendModalService,
    private readonly accountApiService: AccountApiService,
    private readonly usersFilterService: UsersFilterService,
    private readonly usersListService: UsersListService,
    private readonly authUserService: AuthUserService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly invitesService: InvitesService,
    private readonly editUserAccessService: EditUserAccessService,
  ) { }

  ngOnInit(): void {
    const getUsersSub = this.authRestaurantService.select()
      .pipe(
        startWith(null),
        pairwise(),
        filter(([prev, current]) => current.account !== undefined && prev?.account?.id !== current.account?.id),
        tap(([, user]) => this.currentUser = user),
        switchMap(() => combineLatest([
          this.usersListService.load().pipe(switchMap(() => this.usersListService.getUsersAndLocations())),
          this.filters$.pipe(
            startWith(null),
            pairwise(),
            filter(([prev, current]) => prev === null ||
              current.search !== prev.search ||
              current.locationId !== prev.locationId ||
              current.sortBy !== prev.sortBy ||
              current.sortReverse !== prev.sortReverse),
            map(([, current]) => current),
          ),
        ])),
        tap(data => {
          const [[users, locations], filters] = data;
          this.fullUsersList = users;
          this.users = this.usersFilterService.filter(filters, users);
          this.locations = locations;
          this.locationsMap = this.usersListService.getLocationMap();

          for (let user of users) {
            const locationsNames = [];
            for (let locationUser of user.locationUsers) {
              locationsNames.push(this.locationsMap[locationUser.locationId].name);
            }

            this.userLocationsTooltips[user.id] = locationsNames.join(', ');
          }
        }),
      )
      .subscribe();
    this.subscriptions.add(getUsersSub);
    this.subscriptions.add(this.usersListService.listenToChanges().subscribe());
  }

  removeUser(user: UserWithAccount): void {
    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        title: 'Delete user',
        content: `You are about to delete ${user.name} permanently. Do you want to continue?`,
        closeBtnText: 'Decline',
      },
    })
      .pipe(
        take(1),
        filter((res) => res?.response),
        switchMap(() => this.accountApiService.removeAccountUser(user.id)),
        tap(() => {
          this.alertsService.addSuccess(`${user.name}.`, 'Removed: ');
        }),
        catchError(() => {
          this.alertsService.addError(`an error occurred during removing ${user.name} from account.`);

          return of(null);
        }),
      )
      .subscribe();
  }

  editUserAccess(user: UserWithAccount): void {
    this.isEditingUser = true;

    const editSub = this.editUserAccessService.editUserAccess(user)
      .pipe(finalize(() => {
        this.isEditingUser = false;
      }))
      .subscribe();

    this.subscriptions.add(editSub);
  }

  inviteUser(): void {
    this.modalController.create<InviteUserComponent, UserWithAccount | undefined>(InviteUserComponent, {
      currentUser: this.currentUser,
      data: this.usersListService.getUsersAndLocations()
        .pipe(
          combineLatestWith(this.invitesService.selectAll()),
          map(([[users, locations], invites]) => [users, locations, invites]),
        ),
    })
      .pipe(
        take(1),
        filter(data => data !== undefined),
        tap((user: UserWithAccount) => {
          this.editUserAccess(user);
        }),
      )
      .subscribe();
  }

  searchUser(queryString: string): void {
    let filtersData = this.filters$.getValue();
    filtersData = { ...filtersData, search: queryString };

    this.filters$.next(filtersData);
  }

  orderBy({ orderBy, orderAscending }: OrderByValues): void {
    let filterOptions = this.filters$.getValue();

    filterOptions = {
      ...filterOptions,
      sortBy: orderBy,
      sortReverse: orderAscending,
    };
    this.filters$.next(filterOptions);
  }

  filterByLocation(): void {
    this.filters$.next({
      ...this.filters$.getValue(),
      locationId: this.selectedLocationId,
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isAdmin(user: UserWithAccount): boolean {
    return user.role === UserAccountRole.admin;
  }

  buttonDisabled(user: UserWithAccount): boolean {
    return (this.countAdmins === 1 && this.isAdmin(user)) ||
      (this.isAdmin(user) && user.userId === this.currentUser._id);
  }

  get countAdmins(): number {
    return this.fullUsersList.filter(user => user.role === UserAccountRole.admin).length;
  }

  get start(): number {
    return (this.page - 1) * this.pageSize;
  }

  get end(): number {
    return (this.page - 1) * this.pageSize + this.pageSize;
  }
}
